import type { Creature, CreatureState, Stage } from '@/domain/creature/types';
import type { CareEvent } from './types';
import {
  ATTENTION_THRESHOLD,
  FAST_DISCIPLINE_DECAY_MS,
  FAST_HAPPINESS_DECAY_MS,
  FAST_HUNGER_DECAY_MS,
  FAST_POOP_INTERVAL_MS,
  FAST_SICK_DEATH_MS,
  FAST_SICK_HEALTH_DURATION_MS,
  FAST_STAGE_DURATIONS,
  FAST_TANTRUM_TRIGGER_MS,
  HAPPINESS_DECAY_MS,
  HUNGER_DECAY_MS,
  POOP_INTERVAL_MS,
  DISCIPLINE_DECAY_MS,
  SICK_DEATH_MS,
  SICK_HEALTH_DURATION_MS,
  STAGE_DECAY_MULTIPLIER,
  STAGE_DURATIONS,
  TANTRUM_DECAY_BOOST,
  TANTRUM_TRIGGER_MS,
  TICK_MAX_CHUNK_MS,
} from './constants';
import { tickHunger } from './hunger';
import { tickHappiness } from './happiness';
import { tickPoop } from './poop';
import { tickDiscipline, disciplineModifier } from './discipline';
import { tickDisease } from './disease';
import { computeHealth } from './health';
import { deriveCareTraits } from './care-traits';
import { normalizeCareProfile, recordCare } from './profile';
import { diffCareProfile, computeCareScore, resolveCareTier } from './evolution';
import { computeStage, shouldDieOfOldAge } from '@/domain/lifecycle';
import { isSleeping } from '@/domain/sleep';

export interface RunCareTickInput {
  creature: Creature;
  dtMs: number;
  nowMs: number;
  lightOn: boolean;
  timeScale?: number;
  lifecycleTimeScale?: number;
  careTimeScale?: number;
  fastMode?: boolean;
}

export interface RunCareTickResult {
  creature: Creature;
  events: CareEvent[];
}

/**
 * Aplica `dtMs` à criatura. Fatia internamente em chunks de até TICK_MAX_CHUNK_MS
 * para manter regras não-lineares estáveis em catch-up offline.
 */
export function runCareTick(input: RunCareTickInput): RunCareTickResult {
  if (input.creature.state.dead) {
    return { creature: input.creature, events: [] };
  }
  let cur = input.creature;
  let remaining = Math.max(0, input.dtMs);
  const timings = input.fastMode
    ? {
        stageDurations: FAST_STAGE_DURATIONS,
        hungerDecayMs: FAST_HUNGER_DECAY_MS,
        happinessDecayMs: FAST_HAPPINESS_DECAY_MS,
        poopIntervalMs: FAST_POOP_INTERVAL_MS,
        disciplineDecayMs: FAST_DISCIPLINE_DECAY_MS,
        tantrumTriggerMs: FAST_TANTRUM_TRIGGER_MS,
        sickHealthDurationMs: FAST_SICK_HEALTH_DURATION_MS,
        sickDeathMs: FAST_SICK_DEATH_MS,
      }
    : {
        stageDurations: STAGE_DURATIONS,
        hungerDecayMs: HUNGER_DECAY_MS,
        happinessDecayMs: HAPPINESS_DECAY_MS,
        poopIntervalMs: POOP_INTERVAL_MS,
        disciplineDecayMs: DISCIPLINE_DECAY_MS,
        tantrumTriggerMs: TANTRUM_TRIGGER_MS,
        sickHealthDurationMs: SICK_HEALTH_DURATION_MS,
        sickDeathMs: SICK_DEATH_MS,
      };
  const events: CareEvent[] = [];
  // nowMs fornecido é o "fim" do intervalo. nowAt avança chunk a chunk a partir do começo.
  let nowAt = input.nowMs - remaining;

  while (remaining > 0 && !cur.state.dead) {
    const chunk = Math.min(remaining, TICK_MAX_CHUNK_MS);
    nowAt += chunk;
    const r = runChunk(cur, chunk, nowAt, input.lightOn, timings);
    cur = r.creature;
    events.push(...r.events);
    remaining -= chunk;
  }

  // Sempre emite tick no fim
  events.push({ kind: 'tick' });
  return { creature: cur, events };
}

function runChunk(
  creature: Creature,
  dtMs: number,
  nowMs: number,
  _lightOn: boolean,
  timings: {
    stageDurations: typeof STAGE_DURATIONS;
    hungerDecayMs: number;
    happinessDecayMs: number;
    poopIntervalMs: number;
    disciplineDecayMs: number;
    tantrumTriggerMs: number;
    sickHealthDurationMs: number;
    sickDeathMs: number;
  },
): RunCareTickResult {
  const events: CareEvent[] = [];
  const ageMs = nowMs - creature.lifecycle.bornAt;
  const newStage = computeStage(ageMs, timings.stageDurations);
  const oldStage = creature.lifecycle.stage;

  // Atualiza lifecycle
  let lifecycle = creature.lifecycle;
  if (newStage !== oldStage) {
    events.push({ kind: 'stage-changed', from: oldStage, to: newStage });
    lifecycle = {
      ...lifecycle,
      stage: newStage,
      elderSince: newStage === 'elder' && lifecycle.elderSince === null ? nowMs : lifecycle.elderSince,
      careTraits: deriveCareTraits({ ...creature, lifecycle: { ...lifecycle, stage: newStage } }),
    };
  }

  // Snapshot pro placar de evolução — captura só na primeira vez que vira teen.
  if (newStage === 'teen' && lifecycle.teenEntryProfile === null) {
    lifecycle = { ...lifecycle, teenEntryProfile: normalizeCareProfile(creature.careProfile) };
  }

  // Revelação: dispara exatamente uma vez, na primeira vez que a criatura é
  // observada em adult/elder (protege contra saltos grandes de catch-up
  // offline que pulem o estágio teen inteiro sob VITE_FAST_LIFECYCLE).
  if ((newStage === 'adult' || newStage === 'elder') && lifecycle.careTier === null) {
    const delta = diffCareProfile(normalizeCareProfile(creature.careProfile), lifecycle.teenEntryProfile);
    const { score } = computeCareScore(delta);
    const tier = resolveCareTier(score, creature.state, delta);
    lifecycle = { ...lifecycle, careTier: tier, careFinalScore: score };
    events.push({ kind: 'evolved', tier, scoreFinal: score });
  }

  // Egg / Dead: só atualiza stage
  if (newStage === 'egg' || newStage === 'dead') {
    return {
      creature: { ...creature, lifecycle },
      events,
    };
  }

  const sleeping = isSleeping(nowMs);
  const prev = creature.state;
  let careProfile = normalizeCareProfile(creature.careProfile);
  if (prev.sick && !sleeping) {
    careProfile = recordCare(careProfile, { kind: 'sick-time', dtMs });
  }

  // Decay factor combinado
  const decayMul = sleeping
    ? 0
    : STAGE_DECAY_MULTIPLIER[newStage] *
      (1 + disciplineModifier(prev.discipline)) *
      (prev.tantrum ? TANTRUM_DECAY_BOOST : 1);

  // Hunger / Happiness
  const hunger = sleeping ? prev.hunger : tickHunger(prev.hunger, dtMs, decayMul, timings.hungerDecayMs);
  const happiness = sleeping ? prev.happiness : tickHappiness(prev.happiness, dtMs, decayMul, timings.happinessDecayMs);

  // Poop
  const poopRes = sleeping
    ? { poopCount: prev.poopCount, lastPoopAt: prev.lastPoopAt, spawned: false }
    : tickPoop({ poopCount: prev.poopCount, lastPoopAt: prev.lastPoopAt }, dtMs, nowMs, timings.poopIntervalMs);
  if (poopRes.spawned) events.push({ kind: 'poop-spawned' });

  // Discipline + tantrum
  const disc = tickDiscipline({
    state: {
      discipline: prev.discipline,
      tantrum: prev.tantrum,
      tantrumStartedAt: prev.tantrumStartedAt,
      attentionLowSince: prev.attentionLowSince,
    },
    hunger,
    happiness,
    dtMs,
    nowMs,
    sleeping,
    stage: newStage,
    decayMs: timings.disciplineDecayMs,
    tantrumTriggerMs: timings.tantrumTriggerMs,
  });
  if (!prev.tantrum && disc.tantrum) events.push({ kind: 'tantrum-start' });
  if (prev.tantrum && !disc.tantrum) events.push({ kind: 'tantrum-end' });

  // Constrói next state intermediário antes de health/disease
  const intermediate: CreatureState = {
    ...prev,
    hunger,
    happiness,
    poopCount: poopRes.poopCount,
    lastPoopAt: poopRes.lastPoopAt,
    discipline: disc.discipline,
    tantrum: disc.tantrum,
    tantrumStartedAt: disc.tantrumStartedAt,
    attentionLowSince: disc.attentionLowSince,
  };

  const health = computeHealth(intermediate, newStage);

  const disease = tickDisease(
    {
      sick: prev.sick,
      sickSince: prev.sickSince,
      healDoses: prev.healDoses,
      lastHealAt: prev.lastHealAt,
      lowHealthSince: prev.lowHealthSince,
    },
    health,
    poopRes.poopCount,
    nowMs,
    sleeping,
    {
      sickHealthDurationMs: timings.sickHealthDurationMs,
      sickDeathMs: timings.sickDeathMs,
    },
  );
  if (!prev.sick && disease.sick) events.push({ kind: 'sick-start' });
  if (prev.sick && !disease.sick) events.push({ kind: 'cured' });

  // Edge-triggered hunger/happiness events
  emitEdgeAttention(events, prev, intermediate);

  // Determinar morte
  let dead = false;
  let causeOfDeath: 'neglect' | 'old-age' | 'expedition' | null = prev.causeOfDeath;
  if (!sleeping) {
    if (health === 0) {
      dead = true;
      causeOfDeath = 'neglect';
    } else if (disease.dead) {
      dead = true;
      causeOfDeath = 'neglect';
    } else if (newStage === 'elder' && shouldDieOfOldAge(lifecycle, nowMs, timings.stageDurations)) {
      dead = true;
      causeOfDeath = 'old-age';
    }
  }

  if (dead) {
    events.push({ kind: 'died', cause: causeOfDeath === 'old-age' ? 'old-age' : 'neglect' });
  }

  const nextState: CreatureState = {
    ...intermediate,
    sick: disease.sick,
    sickSince: disease.sickSince,
    healDoses: disease.healDoses,
    lastHealAt: disease.lastHealAt,
    lowHealthSince: disease.lowHealthSince,
    dead,
    causeOfDeath,
  };

  return {
    creature: {
      ...creature,
      state: nextState,
      careProfile,
      lifecycle: dead ? { ...lifecycle, stage: 'dead' as Stage } : lifecycle,
    },
    events,
  };
}

function emitEdgeAttention(events: CareEvent[], prev: CreatureState, next: CreatureState): void {
  const wasHungry = prev.hunger < ATTENTION_THRESHOLD;
  const isHungry = next.hunger < ATTENTION_THRESHOLD;
  if (!wasHungry && isHungry) events.push({ kind: 'hungry' });
  if (wasHungry && !isHungry) events.push({ kind: 'hunger-restored' });

  const wasUnhappy = prev.happiness < ATTENTION_THRESHOLD;
  const isUnhappy = next.happiness < ATTENTION_THRESHOLD;
  if (!wasUnhappy && isUnhappy) events.push({ kind: 'unhappy' });
  if (wasUnhappy && !isUnhappy) events.push({ kind: 'happiness-restored' });
}
